/**
 * CharterClassPage — the professor's view of one standalone Charter Class.
 *
 * Everything here is an existing charter component; this file only supplies the
 * class shell and the four tabs:
 *   People & Teams — invite students, then place them into teams
 *   Controls       — CharterRoundControlPanel (start/advance/previous/end/reset)
 *   Monitoring     — CharterMonitoringPanel (live prep + group sessions)
 *   Charters       — the ratified charter per team, viewable and exportable
 *
 * The route param MUST be named `roomId`: CharterMonitoringPanel and
 * CharterViewer both read it straight from `useParams`.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ClipboardList,
  FileText,
  GraduationCap,
  MonitorPlay,
  Users,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TeamsModal } from '@/components/settings/TeamsModal'
import { InviteMemberModal } from '@/components/Room/InviteStudentModal'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { getRoomDetails, getRoomMembers } from '@/redux/asyncThunks/room'
import ROUTES from '@/routes/routes'

import { CharterRoundControlPanel } from '@/features/charter/components/Professor/CharterRoundControlPanel'
import { CharterMonitoringPanel } from '@/features/charter/components/Professor/CharterMonitoringPanel'
import { RatifiedCharterCard } from '@/features/charter/components/Charter/RatifiedCharterCard'
import { CharterCardModal } from '@/features/charter/components/Charter/CharterCardModal'
import { CharterExportButton } from '@/features/charter/components/Charter/CharterExportButton'
import { fetchCharterStatus } from '@/features/charter/redux/asyncThunks/charterThunks'
import { selectTeamStatuses } from '@/features/charter/redux/selectors/charterSelectors'

export default function CharterClassPage() {
  const { roomId = '' } = useParams<{ roomId: string }>()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const currentRoom = useAppSelector((state) => state.room.currentRoom)
  const teamStatuses = useAppSelector(selectTeamStatuses)

  const [isTeamsOpen, setIsTeamsOpen] = useState(false)
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [openCharterTeamId, setOpenCharterTeamId] = useState<number | null>(null)

  // Hydrate currentRoom: the charter WS processors, the invite code readout
  // and the shared modals' read-only gates all depend on it. Refetch when the
  // store holds a PARTIAL room too — the create-room response carries no
  // `status`, and TeamsModal reads `currentRoom.status`, so arriving straight
  // from "Create Charter Class" with only the create payload would lock team
  // management ("cannot be modified after the game has started").
  useEffect(() => {
    if (
      roomId &&
      (currentRoom?.roomid !== roomId || currentRoom.status === undefined)
    ) {
      dispatch(getRoomDetails(roomId))
    }
  }, [dispatch, roomId, currentRoom?.roomid, currentRoom?.status])

  useEffect(() => {
    if (!roomId) return
    dispatch(fetchCharterStatus({ roomId }))
    // The invite modal renders (and duplicate-checks against) the roster from
    // state.room.roomMembers but never fetches it on open — game pages load it
    // when entering a room, so this page must too, or class B's invite modal
    // shows class A's students.
    dispatch(getRoomMembers(roomId))
  }, [dispatch, roomId])

  const inviteCode = currentRoom?.invite_code ?? ''
  const ratifiedTeams = teamStatuses.filter(
    (t) => t.session?.status === 'locked' && t.session?.ratified_charter
  )

  return (
    <div className='p-6'>
      {/* Header */}
      <button
        onClick={() => navigate(ROUTES.PROFESSOR.CHARTER_CLASSES)}
        className='flex gap-1 items-center mb-4 text-sm text-gray-500 hover:text-gray-700'
      >
        <ArrowLeft className='w-4 h-4' />
        All charter classes
      </button>

      <div className='flex flex-wrap gap-4 justify-between items-start mb-6'>
        <div>
          <h1 className='flex gap-2 items-center text-2xl font-semibold text-dark-blue'>
            <GraduationCap className='w-6 h-6 text-indigo-500' />
            {currentRoom?.roomname || 'Charter Class'}
          </h1>
          <p className='mt-1 text-sm text-gray-500'>
            {teamStatuses.length} team{teamStatuses.length === 1 ? '' : 's'} ·{' '}
            {ratifiedTeams.length} charter
            {ratifiedTeams.length === 1 ? '' : 's'} ratified
          </p>
        </div>

        {inviteCode && (
          <div className='px-4 py-3 bg-white rounded-md border'>
            <p className='text-xs font-semibold tracking-wider text-gray-400 uppercase'>
              Join code
            </p>
            <p className='font-mono text-lg font-semibold text-dark-blue'>
              {inviteCode}
            </p>
          </div>
        )}
      </div>

      <Tabs defaultValue='people'>
        <TabsList>
          <TabsTrigger value='people' className='flex gap-2 items-center'>
            <Users className='w-4 h-4' />
            People &amp; Teams
          </TabsTrigger>
          <TabsTrigger value='controls' className='flex gap-2 items-center'>
            <ClipboardList className='w-4 h-4' />
            Controls
          </TabsTrigger>
          <TabsTrigger value='monitoring' className='flex gap-2 items-center'>
            <MonitorPlay className='w-4 h-4' />
            Monitoring
          </TabsTrigger>
          <TabsTrigger value='charters' className='flex gap-2 items-center'>
            <FileText className='w-4 h-4' />
            Charters
          </TabsTrigger>
        </TabsList>

        {/* ── People & Teams ─────────────────────────────────────────────── */}
        <TabsContent value='people' className='mt-4'>
          <div className='p-6 bg-white rounded-md border'>
            <h2 className='mb-1 font-semibold text-dark-blue'>
              Students and teams
            </h2>
            <p className='mb-4 text-sm text-gray-500'>
              Invite students with the join code above, then place them into
              teams. Each team builds one charter.
            </p>
            <div className='flex gap-3'>
              <button
                onClick={() => setIsInviteOpen(true)}
                className='px-4 py-2 text-sm font-medium text-white rounded-md bg-dark-blue hover:opacity-90'
              >
                Invite students
              </button>
              <button
                onClick={() => setIsTeamsOpen(true)}
                className='px-4 py-2 text-sm font-medium rounded-md border text-dark-blue hover:bg-gray-50'
              >
                Manage teams
              </button>
            </div>
          </div>
        </TabsContent>

        {/* ── Controls ───────────────────────────────────────────────────── */}
        <TabsContent value='controls' className='mt-4'>
          <CharterRoundControlPanel roomId={roomId} />
        </TabsContent>

        {/* ── Monitoring ─────────────────────────────────────────────────── */}
        <TabsContent value='monitoring' className='mt-4'>
          <CharterMonitoringPanel inviteCode={inviteCode} />
        </TabsContent>

        {/* ── Charters ───────────────────────────────────────────────────── */}
        <TabsContent value='charters' className='mt-4'>
          {ratifiedTeams.length === 0 ? (
            <div className='py-16 text-center rounded-lg border border-dashed'>
              <FileText className='mx-auto mb-3 w-10 h-10 text-gray-300' />
              <p className='font-medium text-gray-600'>
                No charters ratified yet
              </p>
              <p className='mt-1 text-sm text-gray-400'>
                Ratified charters appear here, ready to view and export.
              </p>
            </div>
          ) : (
            <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
              {ratifiedTeams.map((team) => (
                <div
                  key={team.team_id}
                  className='p-4 bg-white rounded-md border'
                >
                  <div className='flex justify-between items-center mb-3'>
                    <p className='font-semibold text-dark-blue'>
                      {team.team_name}
                    </p>
                    <Badge className='text-emerald-700 bg-emerald-100 border-emerald-200'>
                      Ratified
                    </Badge>
                  </div>
                  <RatifiedCharterCard
                    onClick={() => setOpenCharterTeamId(team.team_id)}
                    charterVersion={team.session?.version ?? 1}
                  />
                  <div className='mt-3'>
                    <CharterExportButton
                      roomId={roomId}
                      teamId={team.team_id}
                      version={team.session?.version}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <TeamsModal
        isOpen={isTeamsOpen}
        onClose={() => {
          setIsTeamsOpen(false)
          // The header's team/charter counts read the charter status, which is
          // otherwise fetched only on mount (its polling lives in the Controls
          // tab) — refetch so a just-created team shows up immediately.
          dispatch(fetchCharterStatus({ roomId }))
        }}
        roomId={roomId}
      />

      <InviteMemberModal
        canInviteProfessors={true}
        isOpen={isInviteOpen}
        onClose={() => {
          setIsInviteOpen(false)
          dispatch(fetchCharterStatus({ roomId }))
        }}
      />

      {openCharterTeamId !== null && (
        <CharterCardModal
          isOpen={true}
          onClose={() => setOpenCharterTeamId(null)}
          roomId={roomId}
          isStudent={false}
          teamId={openCharterTeamId}
        />
      )}
    </div>
  )
}
